
import React, { useEffect, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import Layout from '@/components/Layout/Layout';
import HeroSection from '@/components/Home/HeroSection';
import IntroductionSection from '@/components/Home/IntroductionSection';
import TrustSignals from '@/components/Home/TrustSignals';
import { preloadCriticalResources } from '@/utils/seoEnhancements';
import { preloadCriticalImage, optimizeImageUrl } from '@/utils/imageOptimization';

// Lazy load non-critical components
const HomeServicesSection = lazy(() => import('@/components/Home/HomeServicesSection'));
const ImageGallery = lazy(() => import('@/components/Home/ImageGallery'));
const FeaturedPages = lazy(() => import('@/components/Home/FeaturedPages'));
const SocialMediaCards = lazy(() => import('@/components/Home/SocialMediaCards'));
const CTASection = lazy(() => import('@/components/Home/CTASection'));

const HomePage = () => {
  useEffect(() => {
    // Preload critical resources for performance
    preloadCriticalResources();
    
    // Preload hero image for LCP optimization
    preloadCriticalImage(optimizeImageUrl('https://images.unsplash.com/photo-1632759145351-1d592919f522', {
      width: 1920,
      height: 1080,
      quality: 75
    }));
  }, []);

  return (
    <Layout>
      <Helmet>
        <title>Carolina Trophy Roofs - Premier Roofing Contractor in Eastern North Carolina</title>
        <meta name="description" content="Carolina Trophy Roofs is the premier roofing contractor serving Eastern North Carolina. Expert residential & commercial roofing services in Rocky Mount, Greenville & surrounding areas. Free estimates available - Call (252) 302-2003." />
        <meta name="keywords" content="roofing contractor Rocky Mount NC, roofing contractor Greenville NC, roof replacement Rocky Mount, roof repair Eastern NC, storm damage restoration, insurance claims roofing, commercial roofing NC, residential roofing contractor, best roofer near me, Carolina Trophy Roofs" />
        <link rel="canonical" href="https://carolinatrophyroofs.com/" />
        
        {/* Enhanced Open Graph */}
        <meta property="og:title" content="Carolina Trophy Roofs - Premier Roofing Contractor in Eastern North Carolina" />
        <meta property="og:description" content="Premier roofing contractor in Eastern NC. Expert roof replacement, repair, and storm damage restoration in Rocky Mount, Greenville & surrounding areas." />
        <meta property="og:image" content="https://carolinatrophyroofs.com/lovable-uploads/f93ed555-e5de-49b3-9fde-50a146ff31c8.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:url" content="https://carolinatrophyroofs.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="Carolina Trophy Roofs" />
        
        {/* Twitter Enhanced */}
        <meta name="twitter:title" content="Carolina Trophy Roofs - Premier Roofing Contractor in Eastern North Carolina" />
        <meta name="twitter:description" content="Premier roofing contractor in Eastern NC. Expert roof replacement, repair, and storm damage restoration." />
        
        {/* Local Business Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RoofingContractor",
            "@id": "https://carolinatrophyroofs.com/#organization",
            "name": "Carolina Trophy Roofs",
            "url": "https://carolinatrophyroofs.com",
            "logo": "https://www.carolinatrophyroofs.com/lovable-uploads/78c8c457-224e-406d-8c25-874f1e6b1a0a.png",
            "description": "Eastern North Carolina's premier roofing contractor specializing in residential and commercial roofing services, storm damage restoration, and insurance claims assistance.",
            "telephone": "+1-252-302-2003",
            "email": "Claims@CarolinaTrophyRoofs.com",
            "foundingDate": "2010",
            "address": [
              {
                "@type": "PostalAddress",
                "streetAddress": "136 S. Business Ct",
                "addressLocality": "Rocky Mount",
                "addressRegion": "NC",
                "postalCode": "27804",
                "addressCountry": "US"
              },
              {
                "@type": "PostalAddress", 
                "streetAddress": "1234 Evans St",
                "addressLocality": "Greenville",
                "addressRegion": "NC", 
                "postalCode": "27834",
                "addressCountry": "US"
              }
            ],
            "geo": [
              {
                "@type": "GeoCoordinates",
                "latitude": 35.9382,
                "longitude": -77.7905
              },
              {
                "@type": "GeoCoordinates", 
                "latitude": 35.6127,
                "longitude": -77.3663
              }
            ],
            "areaServed": [
              { "@type": "City", "name": "Rocky Mount", "containedInPlace": { "@type": "State", "name": "North Carolina" }},
              { "@type": "City", "name": "Greenville", "containedInPlace": { "@type": "State", "name": "North Carolina" }},
              { "@type": "City", "name": "Wilson", "containedInPlace": { "@type": "State", "name": "North Carolina" }},
              { "@type": "City", "name": "Zebulon", "containedInPlace": { "@type": "State", "name": "North Carolina" }},
              { "@type": "City", "name": "Tarboro", "containedInPlace": { "@type": "State", "name": "North Carolina" }}
            ],
            "serviceType": ["Roof Replacement", "Roof Repair", "Storm Damage Restoration", "Commercial Roofing", "Residential Roofing", "Insurance Claims"],
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "07:00",
                "closes": "19:00"
              },
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": "Saturday", 
                "opens": "08:00",
                "closes": "17:00"
              }
            ],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Roofing Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Emergency Roof Repair",
                    "description": "24/7 emergency roofing services for storm damage and urgent repairs"
                  }
                },
                {
                  "@type": "Offer", 
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Complete Roof Replacement",
                    "description": "Full roof replacement with premium materials and expert installation"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Commercial Roofing",
                    "description": "Professional commercial roofing services for businesses and industrial buildings"
                  }
                }
              ]
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "247",
              "bestRating": "5",
              "worstRating": "1"
            },
            "contactPoint": [
              {
                "@type": "ContactPoint",
                "telephone": "+1-252-302-2003",
                "contactType": "customer service", 
                "areaServed": "NC",
                "availableLanguage": "English",
                "contactOption": "TollFree"
              }
            ],
            "sameAs": [
              "https://www.facebook.com/carolinatrophyroofs",
              "https://www.instagram.com/carolinatrophyroofs/",
              "https://x.com/RoofsTrophy",
              "https://www.linkedin.com/company/81146474/",
              "https://www.youtube.com/channel/UC8pyyQN0WcMTbT-OHTQbhkA"
            ]
          })}
        </script>
      </Helmet>
      <HeroSection />
      <TrustSignals />
      <IntroductionSection />
      
      {/* Optimized SkyQuote Section */}
      <section className="py-12 bg-gradient-to-br from-blue-50 to-gray-50" id="instant-quote">
        <div className="container mx-auto px-4">
          <div className="text-center mb-6">
            <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full inline-block text-sm font-semibold mb-4">
              ✅ Most Popular - Get Results in Under 60 Seconds
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-brand-charcoal-gray mb-3">
              🏠 Get Your FREE Instant Roofing Quote
            </h3>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Enter your address below for an instant estimate. No personal information required!
            </p>
          </div>
          <div 
            className="w-full max-w-4xl mx-auto bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden"
            style={{ minHeight: '600px' }}
          >
            <iframe 
              src="https://sky-quote.com/instaquote.html?publicKey=67c78ce800214e84a69b&SkyQuoteUrl=https%3A%2F%2Fsky-quote.com"
              width="100%" 
              height="600"
              style={{border: 'none', minHeight: '600px'}}
              allow="geolocation; fullscreen"
              title="Instant Roofing Quote Calculator"
              loading="lazy"
              scrolling="auto"
            />
          </div>
          <div className="text-center mt-6">
            <p className="text-sm text-gray-500">⚡ Instant results • 🔒 No spam • 📞 Optional consultation</p>
          </div>
        </div>
      </section>
      
      <Suspense fallback={<div className="flex justify-center items-center py-16"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-trophy-gold"></div></div>}>
        <HomeServicesSection />
        <ImageGallery />
        <FeaturedPages />
        <SocialMediaCards />
        <CTASection />
      </Suspense>
    </Layout>
  );
};

export default HomePage;
